import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { setStoredActiveOrgSlug, useActiveOrg } from '../active-org.ts';
import {
  useAuth,
  useConnectSource,
  useCreateOrg,
  useStartInstall,
  type CurrentUser,
} from '../api.ts';

import { Layout } from './Layout.tsx';

const SECURITY_URL = 'https://technicalaccounting.net/security';

/**
 * Post-signup onboarding. We no longer prompt for an org name up front —
 * the org is auto-provisioned with a name derived from the user, then
 * the first thing we ask for is the repo link (the actual prerequisite
 * for any data showing up).
 *
 * Flow:
 *   1. Signed-in + 0 orgs → create one with a derived default name
 *      ("Ada's workspace"). The user can rename it later; the connected
 *      repos are what actually matter here.
 *   2. Once an active org exists → the Connect-repo step. The user picks
 *      a provider:
 *        - GitHub → redirect to the GitHub App install URL; GitHub
 *          returns to `/onboarding/connected`, which polls until the
 *          install lands.
 *        - GitLab → an inline form (repo URL + access token) that posts
 *          to the token-source connect endpoint.
 */
export function ConnectRepo() {
  const auth = useAuth();
  const createOrg = useCreateOrg();
  const createStartedRef = useRef(false);

  const orgs = auth.status === 'signed-in' ? auth.me.orgs : [];
  const { active } = useActiveOrg(orgs);

  const provision = () => {
    if (auth.status !== 'signed-in') return;
    createOrg.mutate(
      { name: deriveDefaultOrgName(auth.me.user) },
      { onSuccess: (org) => setStoredActiveOrgSlug(org.slug) },
    );
  };

  // Auto-provision the first org exactly once.
  useEffect(() => {
    if (auth.status !== 'signed-in') return;
    if (auth.me.orgs.length > 0) return;
    if (createStartedRef.current) return;
    createStartedRef.current = true;
    provision();
    // `provision` is stable enough for this once-guarded effect; depend
    // on the auth snapshot so it re-evaluates as auth resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  if (auth.status === 'loading') {
    return (
      <Layout title="Setting up — Technical Accounting">
        <p className="muted">Loading…</p>
      </Layout>
    );
  }

  // Signed-out is handled by the RequireAuth guard; an API error still
  // surfaces here so we don't spin forever.
  if (auth.status === 'error') {
    return (
      <Layout title="Setting up — Technical Accounting">
        <h1 className="view-h1">Couldn&apos;t reach the API</h1>
        <p className="view-subtitle">{auth.message}</p>
      </Layout>
    );
  }

  // Org not ready yet — either the create call is in flight or it failed.
  if (!active) {
    return (
      <Layout title="Setting up — Technical Accounting">
        <h1 className="view-h1">Setting up your workspace…</h1>
        {createOrg.isError ? (
          <>
            <p style={{ color: '#c0392b' }}>{createOrg.error.message}</p>
            <p>
              <button
                type="button"
                className="btn"
                disabled={createOrg.isPending}
                onClick={() => {
                  createStartedRef.current = true;
                  provision();
                }}
              >
                Try again
              </button>
            </p>
          </>
        ) : (
          <p className="muted">One moment while we get things ready.</p>
        )}
      </Layout>
    );
  }

  return <ConnectStep orgSlug={active.slug} />;
}

const centeredColumn: React.CSSProperties = {
  maxWidth: '34rem',
  margin: '0 auto',
  paddingTop: '5rem',
  textAlign: 'center',
};

// Onboarding-specific heading sizes — a touch smaller than the global
// view-h1/view-subtitle defaults so the centered card reads lighter.
const onboardingH1: React.CSSProperties = { fontSize: '32px' };
const onboardingSubtitle: React.CSSProperties = { fontSize: '13px', margin: '0 auto var(--10u)' };

function ConnectStep({ orgSlug }: { orgSlug: string }) {
  const [mode, setMode] = useState<'choose' | 'gitlab'>('choose');

  if (mode === 'gitlab') {
    return <GitLabStep orgSlug={orgSlug} onBack={() => setMode('choose')} />;
  }
  return <ChooseProviderStep orgSlug={orgSlug} onPickGitLab={() => setMode('gitlab')} />;
}

function ChooseProviderStep({
  orgSlug,
  onPickGitLab,
}: {
  orgSlug: string;
  onPickGitLab: () => void;
}) {
  const startInstall = useStartInstall(orgSlug);

  return (
    <Layout title="Connect your repos — Technical Accounting">
      <div style={centeredColumn}>
        <h1 className="view-h1" style={onboardingH1}>Connect your repos</h1>
        <p className="view-subtitle" style={onboardingSubtitle}>
          Install Technical Accounting on the repos you&apos;d like to track. We never push to,
          modify, or write to your repositories. More about security{' '}
          <a href={SECURITY_URL} target="_blank" rel="noreferrer">
            here
          </a>
          .
        </p>
        <div className="row" style={{ justifyContent: 'center' }}>
          <button
            type="button"
            className="btn"
            disabled={startInstall.isPending}
            onClick={() =>
              startInstall.mutate(
                {},
                {
                  onSuccess: ({ install_url }) => {
                    window.location.href = install_url;
                  },
                },
              )
            }
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <GitHubLogo />
            {startInstall.isPending ? 'Opening GitHub…' : 'Connect GitHub'}
          </button>
          <button
            type="button"
            className="btn"
            onClick={onPickGitLab}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <GitLabLogo />
            Connect GitLab
          </button>
        </div>
        {startInstall.isError ? (
          <p style={{ color: '#c0392b', marginTop: 'var(--3u)' }}>{startInstall.error.message}</p>
        ) : null}
      </div>
    </Layout>
  );
}

function GitLabStep({ orgSlug, onBack }: { orgSlug: string; onBack: () => void }) {
  const navigate = useNavigate();
  const connect = useConnectSource(orgSlug);
  const [repoUrl, setRepoUrl] = useState('');
  const [token, setToken] = useState('');
  const [defaultBranch, setDefaultBranch] = useState('');

  const submitDisabled = connect.isPending || !repoUrl || !token;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!repoUrl || !token) return;
    connect.mutate(
      {
        repo_url: repoUrl,
        token,
        default_branch: defaultBranch.trim() || undefined,
      },
      {
        onSuccess: () => {
          navigate('/resilience', { replace: true });
        },
      },
    );
  };

  return (
    <Layout title="Connect GitLab — Technical Accounting">
      <div style={centeredColumn}>
        <h1 className="view-h1" style={onboardingH1}>Connect GitLab</h1>
        <p className="view-subtitle" style={onboardingSubtitle}>
          Add a repo URL as well as an access token with the <code>read_repository</code> scope and
          at least the Reporter role (Guest access will not work).
        </p>
        <form onSubmit={submit} className="stack" style={{ textAlign: 'left' }}>
          <label className="stack" style={{ display: 'flex', flexDirection: 'column' }}>
            Repository URL
            <input
              type="text"
              placeholder="https://gitlab.com/acme/web-app"
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              required
            />
          </label>
          <label className="stack" style={{ display: 'flex', flexDirection: 'column' }}>
            Access token
            <input
              type="password"
              placeholder="glpat-xxxxxxxxxxxxxxxxxxxx"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              autoComplete="off"
              required
            />
          </label>
          <label className="stack" style={{ display: 'flex', flexDirection: 'column' }}>
            <span>
              Default branch <span className="muted">(Optional)</span>
            </span>
            <input
              type="text"
              placeholder="main"
              value={defaultBranch}
              onChange={(e) => setDefaultBranch(e.target.value)}
            />
          </label>
          <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
            <button
              type="submit"
              className="btn"
              disabled={submitDisabled}
              style={submitDisabled ? { borderColor: '#bbb' } : undefined}
            >
              {connect.isPending ? 'Connecting…' : 'Connect Repository'}
            </button>
          </div>
          <p style={{ textAlign: 'center', margin: 0 }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onBack();
              }}
            >
              Go back
            </a>
          </p>
          {connect.isError ? (
            <p style={{ color: '#c0392b' }}>{connect.error.message}</p>
          ) : null}
        </form>
      </div>
    </Layout>
  );
}

/** GitHub octocat mark, monochrome (inherits button text color). */
function GitHubLogo() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M12 .5C5.37.5 0 5.78 0 12.292c0 5.211 3.438 9.63 8.205 11.188.6.111.82-.254.82-.567 0-.28-.01-1.022-.015-2.005-3.338.711-4.042-1.582-4.042-1.582-.546-1.361-1.335-1.725-1.335-1.725-1.087-.731.084-.716.084-.716 1.205.082 1.838 1.215 1.838 1.215 1.07 1.803 2.809 1.282 3.495.981.108-.763.417-1.282.76-1.577-2.665-.295-5.466-1.309-5.466-5.827 0-1.287.465-2.339 1.235-3.164-.135-.298-.54-1.497.105-3.121 0 0 1.005-.316 3.3 1.209.96-.262 1.98-.392 3-.398 1.02.006 2.04.136 3 .398 2.28-1.525 3.285-1.209 3.285-1.209.645 1.624.24 2.823.12 3.121.765.825 1.23 1.877 1.23 3.164 0 4.53-2.805 5.527-5.475 5.817.42.354.81 1.077.81 2.182 0 1.578-.015 2.846-.015 3.229 0 .309.21.678.825.56C20.565 21.917 24 17.495 24 12.292 24 5.78 18.627.5 12 .5z" />
    </svg>
  );
}

/** GitLab tanuki silhouette, monochrome (inherits button text color). */
function GitLabLogo() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="m23.6 9.6-.03-.08-3.26-8.5a.85.85 0 0 0-.84-.54.85.85 0 0 0-.5.18.85.85 0 0 0-.28.43L16.5 6.66H7.5L5.3 1.07a.84.84 0 0 0-.28-.43.85.85 0 0 0-.5-.18.85.85 0 0 0-.84.54L.4 9.52l-.03.08a6.05 6.05 0 0 0 2.01 6.99l.01.01.03.02 4.96 3.72 2.46 1.86 1.5 1.13a1 1 0 0 0 1.2 0l1.5-1.13 2.46-1.86 4.99-3.74.01-.01a6.05 6.05 0 0 0 2-6.99z" />
    </svg>
  );
}

/**
 * A friendly default org name from the signed-in user — first name if we
 * have one, else the email local-part. The user can rename later; this
 * just avoids prompting for a name before they've seen anything.
 */
function deriveDefaultOrgName(user: CurrentUser): string {
  const fromName = user.name?.trim().split(/\s+/)[0];
  const fromEmail = user.email.split('@')[0]?.trim();
  const base = (fromName && fromName.length > 0 ? fromName : fromEmail) || 'My';
  const capitalized = base.charAt(0).toUpperCase() + base.slice(1);
  return `${capitalized}'s workspace`;
}
